import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, Snackbar, Alert, Paper,
  IconButton, InputAdornment, Divider, Stack, Checkbox,
  FormControlLabel, CircularProgress, useTheme, useMediaQuery,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Fade,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Email, Lock, CheckCircle,
  LocalShipping, Shield, TrendingUp, Support, LockReset,
  RestartAlt, ArrowForward,
} from '@mui/icons-material';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { getApiBase } from '../utils/apiConfig';
import MovdLogo from '../components/MovdLogo.jsx';
import { mediaAsset } from '../data/movdMedia.js';

const trustPoints = [
  { icon: <LocalShipping />, title: 'Automatic bookings', body: 'MOVD matches eligible, available movers to customer moves.' },
  { icon: <TrendingUp />, title: 'Run your operation', body: 'Manage availability, capacity, pricing, and bookings in one place.' },
  { icon: <Shield />, title: 'Secure Payments', body: 'Stripe-powered payouts. Get paid fast, every time.' },
  { icon: <Support />, title: 'Dedicated Support', body: 'Real humans on our partner team ready to help you succeed.' },
];

const MoverAuthForm = ({ onAuth }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [recentLogin, setRecentLogin] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('mover_email');
    const remembered = localStorage.getItem('mover_remember_me') === 'true';
    const last = localStorage.getItem('mover_last_login');
    if (stored && remembered) { setEmail(stored); setRememberMe(true); }
    if (last) setRecentLogin(new Date(last).toLocaleDateString());
  }, []);

  const handleForgotPassword = async () => {
    setForgotLoading(true);
    try {
      const res = await axios.post(`${getApiBase()}/auth/forgot-password`, { email: forgotEmail });
      setSnackbar({ open: true, message: res.data?.message || 'Reset instructions sent!', severity: 'success' });
      setForgotOpen(false);
      setForgotEmail('');
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to send reset link.', severity: 'error' });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const loginEmail = email.trim().toLowerCase();
      const res = await axios.post(`${getApiBase()}/movers/login`, { email: loginEmail, password }, { withCredentials: true });

      if (rememberMe) {
        localStorage.setItem('mover_email', loginEmail);
        localStorage.setItem('mover_remember_me', 'true');
        localStorage.setItem('mover_last_login', new Date().toISOString());
      } else {
        localStorage.removeItem('mover_email');
        localStorage.removeItem('mover_remember_me');
      }

      const isAdmin = res?.data?.user?.role === 'admin';
      setSnackbar({
        open: true,
        message: isAdmin ? 'Welcome back, Admin!' : 'Welcome back! Redirecting to your dashboard...',
        severity: 'success',
      });
      onAuth && onAuth(res?.data?.token, res?.data?.user);
    } catch (err) {
      setLoginAttempts(a => a + 1);
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Authentication failed. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#F8FAFC' }}>

        {/* LEFT — Brand Panel (desktop only) */}
        {!isMobile && (
          <Box sx={{
            width: '42%', flexShrink: 0,
            background: `linear-gradient(90deg, rgba(0, 91, 79, .96), rgba(0, 117, 102, .85)), url(${mediaAsset('portal.loginBackdrop').src}) center / cover`,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            px: 6, py: 8, position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, width: 170, height: 170, borderRadius: '0 0 0 100%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />

            <Box sx={{ position: 'relative' }}>
              <MovdLogo size="large" color="white" sx={{ mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, mb: 1, lineHeight: 1.2 }}>
                Mover Partner Portal
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.76)', fontWeight: 400, mb: 5, lineHeight: 1.5 }}>
                Manage requests, bookings, and your business in one place.
              </Typography>

              <Stack spacing={3}>
                {trustPoints.map(pt => (
                  <Box key={pt.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B9F3EA', flexShrink: 0 }}>
                      {pt.icon}
                    </Box>
                    <Box>
                      <Typography fontWeight={700} sx={{ color: 'white', mb: 0.25 }}>{pt.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{pt.body}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)' }}>Not a partner yet?</Typography>
                <Button
                  component={Link} to="/become-partner" endIcon={<ArrowForward />}
                  sx={{ mt: 1, color: '#B9F3EA', textTransform: 'none', fontWeight: 700, p: 0, '&:hover': { bgcolor: 'transparent', color: '#fff' } }}
                >
                  Apply to join Movd
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* RIGHT — Login Form */}
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8 }, py: { xs: 5, md: 8 },
        }}>
          <Fade in timeout={600}>
            <Box sx={{ width: '100%', maxWidth: 440 }}>

              {isMobile && (
                <MovdLogo size="medium" color="primary" sx={{ mb: 2 }} />
              )}

              <Typography variant="h4" fontWeight={800} sx={{ color: '#0F172A', mb: 1, letterSpacing: '-0.02em' }}>
                Sign in
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748B', mb: 4 }}>
                {recentLogin ? `Last login: ${recentLogin}` : 'Welcome back — enter your credentials below.'}
              </Typography>

              <form onSubmit={handleSubmit} noValidate>
                <Stack spacing={2.5}>
                  <TextField
                    label="Email address" type="email" value={email}
                    onChange={e => setEmail(e.target.value)} fullWidth required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Email sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                  />

                  <TextField
                    label="Password" type={showPassword ? 'text' : 'password'} value={password}
                    onChange={e => setPassword(e.target.value)} fullWidth required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={<Checkbox checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} size="small" />}
                      label={<Typography variant="body2" color="text.secondary">Remember me</Typography>}
                    />
                    <Button
                      variant="text" size="small" startIcon={<LockReset fontSize="small" />}
                      onClick={() => setForgotOpen(true)}
                      sx={{ textTransform: 'none', color: '#008D7B', fontWeight: 700, fontSize: '0.85rem' }}
                    >
                      Forgot password?
                    </Button>
                  </Box>

                  {loginAttempts > 2 && (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      Multiple failed attempts. Please double-check your credentials.
                    </Alert>
                  )}

                  <Button
                    type="submit" variant="contained" size="large" fullWidth
                    disabled={loading || !email || !password}
                    sx={{
                      py: 1.8, fontWeight: 700, fontSize: '1rem', borderRadius: 2,
                      bgcolor: '#008D7B', textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#007566', boxShadow: 'none' },
                      transition: 'all 0.2s ease',
                    }}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </Button>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    <Shield sx={{ fontSize: 14, color: '#22C55E' }} />
                    <Typography variant="caption" color="text.secondary">256-bit SSL encrypted</Typography>
                  </Box>

                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">New to Movd?</Typography>
                  </Divider>

                  <Button
                    component={Link} to="/become-partner" variant="outlined" fullWidth size="large"
                    endIcon={<ArrowForward />}
                    sx={{
                      py: 1.6, fontWeight: 600, borderRadius: 2, textTransform: 'none',
                      borderColor: '#E2E8F0', color: '#0F172A', bgcolor: 'white',
                      '&:hover': { borderColor: '#008D7B', color: '#008D7B', bgcolor: '#F0FDFA' },
                    }}
                  >
                    Apply to become a partner
                  </Button>
                </Stack>
              </form>

              {isMobile && (
                <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['Licensed & Verified', 'Fast Payouts', 'Automatic bookings', '24/7 Support'].map(label => (
                    <Chip key={label} icon={<CheckCircle sx={{ fontSize: '14px !important' }} />} label={label}
                      size="small" sx={{ bgcolor: '#E1F7F3', color: '#007566', border: '1px solid #B9E9E1', fontWeight: 600, '& .MuiChip-icon': { color: '#008D7B' } }} />
                  ))}
                </Box>
              )}
            </Box>
          </Fade>
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: '#E1F7F3', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <RestartAlt sx={{ color: '#008D7B' }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>Reset your password</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            We'll send reset instructions to your email
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Email address" type="email" value={forgotEmail}
            onChange={e => setForgotEmail(e.target.value)} fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }}
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setForgotOpen(false)} sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>Cancel</Button>
          <Button
            onClick={handleForgotPassword} variant="contained"
            disabled={forgotLoading || !forgotEmail}
            sx={{ borderRadius: 1, textTransform: 'none', flex: 1, bgcolor: '#008D7B' }}
          >
            {forgotLoading ? <CircularProgress size={18} /> : 'Send link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2, minWidth: 300 }} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MoverAuthForm;
